import { CoverTemplate, CoverTemplateValues } from '../types';
import { getDefaultCoverValues, buildDocumentFromCoverSections } from './templateUtils';
import { DocumentSection, TextBlock, ImageBlock, TableBlock } from '../../core/ast/document.types';

export const standardLabReportCoverTemplate: CoverTemplate = {
  id: 'tpl-standard-lab-report',
  name: 'Standard EWU Lab Report',
  category: 'lab_report',
  description: 'Exact standard East West University lab report format with top logo header, LAB REPORT banner, details box, and submitted to/by sections.',
  thumbnailBadge: 'EWU Standard',
  version: 2,
  supportedFields: [
    'universityName', 'universityAddress', 'departmentName', 'logoAssetId', 'showLogo',
    'documentTypeLabel', 'title', 'subtitle',
    'courseCode', 'courseTitle', 'sectionOrGroup',
    'studentName', 'studentId', 'facultyName', 'facultyDesignation',
    'submissionDate', 'semester', 'academicYear',
  ],
  defaultValues: getDefaultCoverValues({
    documentTypeLabel: 'LAB REPORT',
    title: 'LAB REPORT',
    courseCode: '',
    courseTitle: '',
    semester: '',
    facultyName: '',
    facultyDesignation: '',
    departmentName: '',
    studentName: '',
    studentId: '',
    sectionOrGroup: '',
    submissionDate: '',
    borderStyle: 'none',
  }),
  compileToDocument: (values: CoverTemplateValues, pageSettings) => {
    const now = new Date().toISOString();
    const blocks: Array<TextBlock | ImageBlock | TableBlock> = [];

    // Header with EWU Logo and Institution Text
    if (values.showLogo && values.logoAssetId) {
      blocks.push({
        id: 'blk-std-logo',
        type: 'image',
        createdAt: now,
        data: {
          assetId: values.logoAssetId,
          previewDataUrl: values.logoAssetId,
          widthPercent: 28,
          alignment: 'center',
          altText: values.universityName || 'East West University',
        },
      });
    }

    blocks.push({
      id: 'blk-std-univ',
      type: 'text',
      createdAt: now,
      data: {
        text: (values.universityName || 'EAST WEST UNIVERSITY').toUpperCase(),
        formatting: { bold: true, fontSizePt: 16, alignment: 'center', color: '#1a365d' },
      },
    });

    blocks.push({
      id: 'blk-std-addr',
      type: 'text',
      createdAt: now,
      data: {
        text: values.universityAddress || 'A/2 Jahurul Islam Ave, Dhaka 1212',
        formatting: { fontSizePt: 10, alignment: 'center', color: '#4a5563' },
      },
    });

    // Space before Main LAB REPORT Banner
    blocks.push({
      id: 'blk-std-sp1',
      type: 'text',
      createdAt: now,
      data: {
        text: '\n\n',
        formatting: { fontSizePt: 12 },
      },
    });

    // Main LAB REPORT Banner
    blocks.push({
      id: 'blk-std-title',
      type: 'text',
      createdAt: now,
      data: {
        text: values.documentTypeLabel || 'LAB REPORT',
        formatting: {
          bold: true,
          fontSizePt: 24,
          alignment: 'center',
          color: '#3182ce',
          underline: true,
        },
      },
    });

    // Space before Details section
    blocks.push({
      id: 'blk-std-sp2',
      type: 'text',
      createdAt: now,
      data: {
        text: '\n\n',
        formatting: { fontSizePt: 12 },
      },
    });

    // DETAILS Section
    blocks.push({
      id: 'blk-std-sec-details',
      type: 'text',
      createdAt: now,
      data: {
        text: 'DETAILS:\n-------',
        formatting: { bold: true, fontSizePt: 12, color: '#3182ce' },
      },
    });

    const labNoLine = values.subtitle ? `Lab No : ${values.subtitle}` : 'Lab No : ';
    const courseCodeLine = `Course Code   :   ${values.courseCode || ''}`;
    const courseTitleLine = `Course Title    :   ${values.courseTitle || ''}`;
    const semesterLine = `Semester         :   ${values.semester || ''}`;

    blocks.push({
      id: 'blk-std-details-content',
      type: 'text',
      createdAt: now,
      data: {
        text: `${labNoLine}\n${courseCodeLine}\n${courseTitleLine}\n${semesterLine}\n`,
        formatting: { fontSizePt: 11, color: '#2d3748' },
      },
    });

    // SUBMITTED TO Section
    blocks.push({
      id: 'blk-std-sec-subto',
      type: 'text',
      createdAt: now,
      data: {
        text: 'SUBMITTED TO:\n------------',
        formatting: { bold: true, fontSizePt: 12, color: '#3182ce' },
      },
    });

    const teacherName = values.facultyName ? values.facultyName.toUpperCase() : '';
    const designation = values.facultyDesignation ? `${values.facultyDesignation.toUpperCase()},` : '';
    const dept = values.departmentName || '';

    blocks.push({
      id: 'blk-std-subto-content',
      type: 'text',
      createdAt: now,
      data: {
        text: `${teacherName}\n${designation ? designation + '\n' : ''}${dept}\n`,
        formatting: { fontSizePt: 11, bold: false, color: '#2d3748' },
      },
    });

    // SUBMITTED BY Section
    blocks.push({
      id: 'blk-std-sec-subby',
      type: 'text',
      createdAt: now,
      data: {
        text: 'SUBMITTED BY:\n------------',
        formatting: { bold: true, fontSizePt: 12, color: '#3182ce' },
      },
    });

    const studentNameLine = `Name               :   ${values.studentName || ''}`;
    const studentIdLine = `Student ID      :   ${values.studentId || ''}`;
    const sectionLine = `Section            :   ${values.sectionOrGroup || ''}`;
    const dateLine = `Submission Date :   ${values.submissionDate || ''}`;

    blocks.push({
      id: 'blk-std-subby-content',
      type: 'text',
      createdAt: now,
      data: {
        text: `${studentNameLine}\n${studentIdLine}\n${sectionLine}\n\n${dateLine}`,
        formatting: { fontSizePt: 11, color: '#2d3748' },
      },
    });

    const coverSection: DocumentSection = {
      id: 'sec-cover',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      settings: { pageBreakBefore: false },
      blocks,
    };

    return buildDocumentFromCoverSections(values, [coverSection], pageSettings);
  },
};
